import React from "react";

/**
 * Field — a wrapper for any form control that can optionally carry
 * field-level tools. Three tool placements:
 *
 *   labelTools   — icons beside the label (info, help, lock)
 *   inlineTools  — icons rendered INSIDE the input on the right
 *                  (lookup, clear, calendar, calculator, copy-previous)
 *   suffixTools  — tools rendered AFTER the input (buttons attached to field)
 *
 * Use this everywhere you render a form field so behavior stays uniform.
 *
 * Example:
 *   <Field
 *     label="Customer"
 *     required
 *     labelTools={<ToolbarButton icon="bi-info-circle" tooltip="..." />}
 *     inlineTools={
 *       <>
 *         <ToolbarButton icon="bi-search" tooltip="Lookup" onClick={openLookup} />
 *         <ToolbarButton icon="bi-x" tooltip="Clear" onClick={clear} />
 *       </>
 *     }
 *   >
 *     <Form.Control value={value} onChange={...} />
 *   </Field>
 */
const Field = ({
  label,
  required = false,
  hint,
  error,
  labelTools,
  inlineTools,
  suffixTools,
  children,
  className = ""
}) => {
  return (
    <div className={`ds-field ${inlineTools ? "ds-field--has-inline-tools" : ""} ${className}`}>
      {label && (
        <div className="ds-field__label">
          <span>
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </span>
          {labelTools && <span className="ds-field__label-tools">{labelTools}</span>}
        </div>
      )}

      <div className="d-flex gap-1 align-items-stretch">
        <div className="ds-field__control flex-grow-1">
          {children}
          {inlineTools && (
            <span className="ds-field__inline-tools" onClick={(e) => e.stopPropagation()}>
              {inlineTools}
            </span>
          )}
        </div>
        {suffixTools && (
          <div className="d-flex gap-1 align-items-center">{suffixTools}</div>
        )}
      </div>

      {hint && !error && (
        <div style={{ fontSize: "var(--ds-fs-xs)", color: "var(--ds-text-muted)", marginTop: 2 }}>
          {hint}
        </div>
      )}
      {error && (
        <div style={{ fontSize: "var(--ds-fs-xs)", color: "var(--ds-danger)", marginTop: 2 }}>
          <i className="bi bi-exclamation-circle me-1" />
          {error}
        </div>
      )}
    </div>
  );
};

export default Field;
